import type { ReactNode } from 'react';

import { cx } from '../cx';

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

// Nunca so a cor: o simbolo e o texto dizem o mesmo (regra do painel do monitor).
const looks: Record<StatusTone, { className: string; symbol: string }> = {
  success: { className: 'border-gu-success/60 bg-gu-success/10 text-gu-success', symbol: '✓' },
  warning: { className: 'border-gu-warning/60 bg-gu-warning/10 text-gu-warning', symbol: '!' },
  danger: { className: 'border-gu-danger/60 bg-gu-danger/10 text-gu-danger', symbol: '✕' },
  info: { className: 'border-gu-info/60 bg-gu-info/10 text-gu-info', symbol: 'i' },
  neutral: { className: 'border-gu-line bg-gu-raised text-gu-ink-muted', symbol: '•' },
};

export type StatusBadgeProps = {
  tone: StatusTone;
  children: ReactNode;
  /** Troca o simbolo por defeito do tom. */
  symbol?: string;
  className?: string;
};

/** Selo de estado do back-office (pago, bloqueado, pendente). Nao usar no site publico. */
export function StatusBadge({ tone, children, symbol, className }: StatusBadgeProps) {
  const look = looks[tone];
  return (
    <span
      className={cx(
        'inline-flex w-fit items-center gap-1.5 border px-2 py-0.5 text-caption font-semibold',
        look.className,
        className,
      )}
    >
      <span aria-hidden="true">{symbol ?? look.symbol}</span>
      {children}
    </span>
  );
}

export type NoticeProps = {
  tone: StatusTone;
  title?: string;
  children?: ReactNode;
  className?: string;
};

/**
 * Aviso em bloco. `danger` e `warning` anunciam-se logo (role=alert); os outros
 * esperam pela pausa do leitor de ecra (role=status).
 */
export function Notice({ tone, title, children, className }: NoticeProps) {
  const look = looks[tone];
  const urgent = tone === 'danger' || tone === 'warning';
  return (
    <div
      role={urgent ? 'alert' : 'status'}
      className={cx('flex gap-3 border-l-rule px-4 py-3 text-body', look.className, className)}
    >
      <span aria-hidden="true" className="font-bold">
        {look.symbol}
      </span>
      <div className="flex flex-col gap-0.5">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="text-gu-ink">{children}</div> : null}
      </div>
    </div>
  );
}
